/**
 * Machines tab — unified cloud-instance + orchestrator table.
 *
 * Rows come from three sources:
 *   1. Cloud instances (joined with orchestrators when connected)
 *   2. Managed orchestrators connected but missing from the instance list (orphans)
 *   3. Unmanaged (external) orchestrators
 */

import { Fragment, useCallback, useState } from 'react';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import {
  orchestratorStateBadgeClass,
  provisioningBadgeClass,
  relativeTime,
  type EnrichedInstance,
  type OrchestratorInfo,
} from './shared';
import type { PoolConfigDraftApi } from './usePoolConfigDraft';

// Number of columns in the table — the app-detail row spans all of them.
const ORCH_TABLE_COLSPAN = 13;

function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d`;
}

/** Saved reaper tuning, derived from the shared config draft. */
interface ReaperView {
  enabled: boolean;
  graceMs: number;
  immunityMs: number;
}

// The "Idle" cell: active now / idle duration, plus a reap-eligibility
// estimate for workers the drain reaper could take. Eligibility ≠ certain
// termination — floor protection and the per-tick valve can hold a worker
// past its eligible time, so overdue renders as "reap due" rather than a
// negative countdown.
function IdleCell({ orch, reaper }: { orch: OrchestratorInfo; reaper: ReaperView | null }) {
  if (orch.activeSessions > 0) {
    return <span>active now</span>;
  }
  if (!orch.idleSince) {
    return <span style={{ color: 'var(--text-tertiary)' }}>-</span>;
  }
  const now = Date.now();
  const idleMs = now - Date.parse(orch.idleSince);
  const reapable = reaper?.enabled && orch.managed && !orch.pinned && orch.state !== 'draining';
  let reapNote: string | null = null;
  if (reapable && reaper) {
    const eligibleAt = Math.max(
      Date.parse(orch.idleSince) + reaper.graceMs,
      Date.parse(orch.registeredAt) + reaper.immunityMs,
    );
    reapNote = eligibleAt <= now ? 'reap due' : `reap ~${fmtDuration(eligibleAt - now)}`;
  }
  return (
    <span title={`Idle since ${orch.idleSince}${reapNote ? ' — reap time is an estimate; floor protection and the per-tick valve can delay it' : ''}`}>
      idle {fmtDuration(idleMs)}
      {reapNote && (
        <span style={{ display: 'block', fontSize: 'var(--text-xs)', color: reapNote === 'reap due' ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>
          {reapNote}
        </span>
      )}
    </span>
  );
}

export interface MachinesTabProps {
  instances: EnrichedInstance[];
  orchestrators: OrchestratorInfo[];
  /** Shared config draft — the orchestrator-version pin edits it. */
  draftApi: PoolConfigDraftApi;
  saving: boolean;
  /** Pool is deactivated: mutating controls disable, inspection stays live. */
  dormant?: boolean;
  orchestratorReleases: Array<{ version: string; releasedAt: string }>;
  actionInProgress: string | null;
  onDrain: (id: string) => void;
  onTerminate: (id: string) => void;
  onPin: (id: string, pinned: boolean) => void;
  onReboot: (instanceId: string) => void;
  onTerminateInstance: (instanceId: string) => void;
  onRefresh: () => void;
  onRefreshDesiredState: () => void;
  refreshDesiredStateLoading: boolean;
  refreshDesiredStateData: unknown;
}

export function MachinesTab({
  instances,
  orchestrators,
  draftApi,
  saving,
  dormant = false,
  orchestratorReleases,
  actionInProgress,
  onDrain,
  onTerminate,
  onPin,
  onReboot,
  onTerminateInstance,
  onRefresh,
  onRefreshDesiredState,
  refreshDesiredStateLoading,
  refreshDesiredStateData,
}: MachinesTabProps) {
  const [refreshing, setRefreshing] = useState(false);
  // Track which rows have their app-version detail expanded, keyed by the
  // row's React key so every row source (instance / orphan / external) is
  // addressable.
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const toggleApps = useCallback((rowId: string) => {
    setExpandedApps(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      onRefresh();
    } finally {
      // Brief visual feedback then reset
      setTimeout(() => setRefreshing(false), 600);
    }
  };

  const orchByWorkerId = new Map<string, OrchestratorInfo>();
  for (const o of orchestrators) {
    orchByWorkerId.set(o.workerId, o);
  }

  const connectedOrchestratorIds = new Set<string>();
  for (const inst of instances) {
    if (inst.orchestratorId) {
      connectedOrchestratorIds.add(inst.orchestratorId);
    }
  }
  const unmanagedOrchestrators = orchestrators.filter(
    o => !connectedOrchestratorIds.has(o.workerId) && !o.managed
  );
  const managedOrphanOrchestrators = orchestrators.filter(
    o => !connectedOrchestratorIds.has(o.workerId) && o.managed
  );

  const totalCount = instances.length + unmanagedOrchestrators.length + managedOrphanOrchestrators.length;

  // Inline status for the last refresh-desired-state action, surfaced next
  // to the button so operators can see at a glance whether the broadcast
  // landed without scrolling away from the table they were diagnosing.
  const refreshStatus = (() => {
    if (refreshDesiredStateLoading) return null;
    const d = refreshDesiredStateData as
      | { ok?: boolean; sockets?: number; configRevision?: number; error?: string }
      | null;
    if (!d) return null;
    if (d.ok) {
      return {
        text: `Broadcast → ${d.sockets ?? 0} sockets · rev ${d.configRevision ?? 0}`,
        color: 'var(--color-success)',
      };
    }
    return {
      text: d.error ? `Failed: ${d.error}` : 'Broadcast failed',
      color: 'var(--color-error)',
    };
  })();

  const { draft, patch } = draftApi;

  // Saved reaper tuning for the reap-eligibility estimate. Unsaved edits in
  // the draft shift the estimate until saved — acceptable for a preview.
  const reaperView: ReaperView | null = draft
    ? {
        enabled: draft.reaperEnabled,
        graceMs: (parseInt(draft.reaperIdleGraceSeconds, 10) || 720) * 1000,
        immunityMs: (parseInt(draft.reaperBootImmunitySeconds, 10) || 720) * 1000,
      }
    : null;

  return (
    <>
    {/* Orchestrator version pin — which orc build managed instances launch
        with. Edits the shared config draft; the page-level save bar appears
        when dirty. Never auto-upgraded on running machines: a changed pin
        applies to instances provisioned after save. */}
    {draft && (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Orchestrator Version</h2>
        </div>
        <div className={styles.formHelp}>
          The orchestrator build newly provisioned machines launch with. Running machines
          keep their current version until replaced.
        </div>
        <div className={styles.formRow}>
          <select
            id="orc-version"
            aria-label="Orchestrator Version"
            className={styles.formSelect}
            value={draft.orchestratorVersion}
            onChange={e => patch({ orchestratorVersion: e.target.value })}
            disabled={saving || dormant}
          >
            <option value="">Latest</option>
            {orchestratorReleases.map(r => (
              <option key={r.version} value={r.version}>
                v{r.version} ({new Date(r.releasedAt).toLocaleDateString()})
              </option>
            ))}
          </select>
        </div>
      </div>
    )}

    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          Machines &amp; Orchestrators ({totalCount})
        </h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {refreshStatus && (
            <span style={{ fontSize: 'var(--text-xs)', color: refreshStatus.color }}>
              {refreshStatus.text}
            </span>
          )}
          <button
            className={styles.cautionButton}
            onClick={onRefreshDesiredState}
            disabled={refreshDesiredStateLoading || dormant}
            title="Re-broadcast PoolDO's cached desired-state with a bumped revision. Wakes up orcs stuck in RECONCILING (typically APPS=0 with active heartbeats — post-wipe steady state). Cheaper than rematerialize+push: skips the resolver round-trip. Use this first when workers are wedged; only escalate to rematerialize if the cached config itself is suspect."
          >
            {refreshDesiredStateLoading ? 'Refreshing…' : 'Refresh Desired State'}
          </button>
          <span className={styles.refreshIndicator}>Auto-refresh 10s</span>
          <button
            className={styles.actionButton}
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ opacity: refreshing ? 0.5 : 1 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {totalCount === 0 ? (
        <div className={styles.empty}>No machines or orchestrators</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Machine ID</th>
              <th>Backend</th>
              <th>Machine Status</th>
              <th>Orchestrator</th>
              <th>State</th>
              <th>Region</th>
              <th>Sessions</th>
              <th>Apps</th>
              <th>Connected</th>
              <th>Idle</th>
              <th>Last Ping</th>
              <th>Flags</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {/* Cloud instances (with or without connected orchestrators) */}
            {instances.map(inst => {
              const orch = inst.orchestratorId
                ? orchByWorkerId.get(inst.orchestratorId) ?? null
                : null;
              const rowId = `inst-${inst.instanceId}`;
              const appsExpanded = expandedApps.has(rowId);
              return (
                <Fragment key={rowId}>
                <tr>
                  <td>
                    <div>{inst.instanceId}</div>
                    {inst.publicIp && (
                      <a href={`vnc://${inst.publicIp}`} style={{ fontSize: 'var(--text-xs)', opacity: 0.6, color: 'inherit' }}>{inst.publicIp}</a>
                    )}
                  </td>
                  <td>{inst.backendId}</td>
                  <td>
                    <span className={provisioningBadgeClass(
                      inst.status === 'RUNNING' || inst.status === 'InService' ? 'provisioned' :
                      inst.status === 'TERMINATED' || inst.status === 'Terminating' ? 'failed' : 'pending'
                    )}>
                      {inst.status}
                    </span>
                  </td>
                  <td>{orch ? orch.workerId : <span style={{ color: 'var(--text-tertiary)' }}>not connected</span>}</td>
                  <td>
                    {orch ? (
                      <span className={orchestratorStateBadgeClass(orch.state)}>{orch.state}</span>
                    ) : (
                      <span style={{ color: 'var(--text-tertiary)' }}>-</span>
                    )}
                  </td>
                  <td>{orch ? `${orch.region}${orch.zone ? `/${orch.zone}` : ''}` : '-'}</td>
                  <td>{orch ? `${orch.activeSessions}/${orch.maxConcurrentSessions}` : '-'}</td>
                  <td>{orch ? <AppsCell orch={orch} rowId={rowId} expanded={appsExpanded} onToggle={toggleApps} /> : '-'}</td>
                  <td>{orch ? <span title={orch.registeredAt}>{relativeTime(orch.registeredAt)}</span> : '-'}</td>
                  <td>{orch ? <IdleCell orch={orch} reaper={reaperView} /> : '-'}</td>
                  <td>{orch ? <span title={orch.lastPingAt}>{relativeTime(orch.lastPingAt)}</span> : '-'}</td>
                  <td>
                    {orch && (
                      <>
                        {orch.pinned && <span className={styles.pinnedIcon} title="Pinned">PIN</span>}
                        <span className={styles.managedIcon} title="Managed orchestrator">MGD</span>
                      </>
                    )}
                  </td>
                  <td>
                    <div className={styles.workerActions}>
                      <button
                        className={styles.actionButton}
                        onClick={() => onReboot(inst.instanceId)}
                        disabled={!!actionInProgress || dormant}
                        title="Reboot cloud instance — the OS restarts and the orchestrator reconnects"
                      >
                        Reboot
                      </button>
                      <button
                        className={styles.dangerButton}
                        onClick={() => onTerminateInstance(inst.instanceId)}
                        disabled={!!actionInProgress || dormant}
                        title="Terminate cloud instance (pool will replace to keep desired capacity)"
                      >
                        Terminate
                      </button>
                      {orch && (
                        <>
                          {orch.state !== 'draining' && (
                            <button
                              className={styles.actionButton}
                              onClick={() => onDrain(orch.workerId)}
                              disabled={!!actionInProgress || dormant}
                              title="Drain orchestrator — finish current sessions, take no new ones"
                            >
                              Drain
                            </button>
                          )}
                          <button
                            className={styles.dangerButton}
                            onClick={() => onTerminate(orch.workerId)}
                            disabled={!!actionInProgress || dormant}
                            title="Force-disconnect the orchestrator"
                          >
                            Kill
                          </button>
                          <button
                            className={styles.actionButton}
                            onClick={() => onPin(orch.workerId, !orch.pinned)}
                            disabled={!!actionInProgress || dormant}
                            title={orch.pinned ? 'Unpin — allow scale-in to reclaim this worker' : 'Pin — protect this worker from scale-in'}
                          >
                            {orch.pinned ? 'Unpin' : 'Pin'}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
                {orch && appsExpanded && <OrchAppsDetailRow orch={orch} colSpan={ORCH_TABLE_COLSPAN} />}
                </Fragment>
              );
            })}

            {/* Managed orchestrators connected but not in instance list (orphans) */}
            {managedOrphanOrchestrators.map(w => {
              const rowId = `orch-${w.workerId}`;
              const appsExpanded = expandedApps.has(rowId);
              return (
              <Fragment key={rowId}>
              <tr>
                <td style={{ color: 'var(--text-tertiary)' }}>{w.instanceId || 'unknown'}</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td>{w.workerId}</td>
                <td><span className={orchestratorStateBadgeClass(w.state)}>{w.state}</span></td>
                <td>{w.region}{w.zone ? `/${w.zone}` : ''}</td>
                <td>{w.activeSessions}/{w.maxConcurrentSessions}</td>
                <td><AppsCell orch={w} rowId={rowId} expanded={appsExpanded} onToggle={toggleApps} /></td>
                <td title={w.registeredAt}>{relativeTime(w.registeredAt)}</td>
                <td><IdleCell orch={w} reaper={reaperView} /></td>
                <td title={w.lastPingAt}>{relativeTime(w.lastPingAt)}</td>
                <td>
                  {w.pinned && <span className={styles.pinnedIcon} title="Pinned">PIN</span>}
                  <span className={styles.managedIcon} title="Managed orchestrator">MGD</span>
                </td>
                <td>
                  <div className={styles.workerActions}>
                    {w.state !== 'draining' && (
                      <button className={styles.actionButton} onClick={() => onDrain(w.workerId)} disabled={!!actionInProgress} title="Drain">Drain</button>
                    )}
                    <button className={styles.dangerButton} onClick={() => onTerminate(w.workerId)} disabled={!!actionInProgress} title="Terminate">Kill</button>
                    <button className={styles.actionButton} onClick={() => onPin(w.workerId, !w.pinned)} disabled={!!actionInProgress} title={w.pinned ? 'Unpin' : 'Pin'}>{w.pinned ? 'Unpin' : 'Pin'}</button>
                  </div>
                </td>
              </tr>
              {appsExpanded && <OrchAppsDetailRow orch={w} colSpan={ORCH_TABLE_COLSPAN} />}
              </Fragment>
              );
            })}

            {/* Unmanaged (external) orchestrators */}
            {unmanagedOrchestrators.map(w => {
              const rowId = `ext-${w.workerId}`;
              const appsExpanded = expandedApps.has(rowId);
              return (
              <Fragment key={rowId}>
              <tr>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td style={{ color: 'var(--text-tertiary)' }}>-</td>
                <td>{w.workerId}</td>
                <td><span className={orchestratorStateBadgeClass(w.state)}>{w.state}</span></td>
                <td>{w.region}{w.zone ? `/${w.zone}` : ''}</td>
                <td>{w.activeSessions}/{w.maxConcurrentSessions}</td>
                <td><AppsCell orch={w} rowId={rowId} expanded={appsExpanded} onToggle={toggleApps} /></td>
                <td title={w.registeredAt}>{relativeTime(w.registeredAt)}</td>
                <td><IdleCell orch={w} reaper={reaperView} /></td>
                <td title={w.lastPingAt}>{relativeTime(w.lastPingAt)}</td>
                <td>
                  <span className={styles.unmanagedIcon} title="Unmanaged — not provisioned by pool infrastructure">EXT</span>
                  {w.pinned && <span className={styles.pinnedIcon} title="Pinned">PIN</span>}
                </td>
                <td>
                  <div className={styles.workerActions}>
                    {w.state !== 'draining' && (
                      <button className={styles.actionButton} onClick={() => onDrain(w.workerId)} disabled={!!actionInProgress} title="Drain">Drain</button>
                    )}
                    <button className={styles.dangerButton} onClick={() => onTerminate(w.workerId)} disabled={!!actionInProgress} title="Terminate">Kill</button>
                    <button className={styles.actionButton} onClick={() => onPin(w.workerId, !w.pinned)} disabled={!!actionInProgress} title={w.pinned ? 'Unpin' : 'Pin'}>{w.pinned ? 'Unpin' : 'Pin'}</button>
                  </div>
                </td>
              </tr>
              {appsExpanded && <OrchAppsDetailRow orch={w} colSpan={ORCH_TABLE_COLSPAN} />}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
    </>
  );
}

// The "Apps" cell: shows the provisioned-app count and, when there is at least
// one app, doubles as a disclosure toggle for the per-version detail row.
function AppsCell({
  orch,
  rowId,
  expanded,
  onToggle,
}: {
  orch: OrchestratorInfo;
  rowId: string;
  expanded: boolean;
  onToggle: (rowId: string) => void;
}) {
  const count = orch.provisionedApps.length;
  if (count === 0) return <span style={{ color: 'var(--text-tertiary)' }}>0</span>;
  return (
    <button
      type="button"
      onClick={() => onToggle(rowId)}
      aria-expanded={expanded}
      aria-label={`${count} app version${count === 1 ? '' : 's'}`}
      title={expanded ? 'Hide app versions' : 'Show app versions'}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'inherit',
        font: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-1)',
      }}
    >
      {count}
      <span aria-hidden="true" style={{ fontSize: '0.7em', color: 'var(--text-tertiary)' }}>
        {expanded ? '▾' : '▸'}
      </span>
    </button>
  );
}

// Detail row rendered beneath an expanded orchestrator: one line per
// provisioned app showing appId:versionId, ready/baking state, and how many
// of the orc's activeJobs are currently running that app.
function OrchAppsDetailRow({ orch, colSpan }: { orch: OrchestratorInfo; colSpan: number }) {
  const activeByApp = new Map<string, number>();
  // Sessions whose reported versionId differs from the currently provisioned
  // version for that app (e.g. sessions started before an app-version
  // rotation). Only counted when the orc reports versionId on the job —
  // older orcs omit it and we attribute to the provisioned version.
  const staleVersionByApp = new Map<string, number>();
  for (const job of orch.activeJobs) {
    activeByApp.set(job.appId, (activeByApp.get(job.appId) ?? 0) + 1);
    const provisioned = orch.provisionedApps.find(a => a.appId === job.appId);
    if (job.versionId && provisioned && job.versionId !== provisioned.versionId) {
      staleVersionByApp.set(job.appId, (staleVersionByApp.get(job.appId) ?? 0) + 1);
    }
  }
  const apps = [...orch.provisionedApps].sort((a, b) =>
    a.appId === b.appId ? a.versionId.localeCompare(b.versionId) : a.appId.localeCompare(b.appId)
  );
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-1)',
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--bg-tertiary)',
          }}
        >
          {apps.map(app => {
            const active = activeByApp.get(app.appId) ?? 0;
            return (
              <div
                key={`${app.appId}:${app.versionId}`}
                style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-xs)' }}
              >
                <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
                  {app.appId}:{app.versionId}
                </span>
                <span className={provisioningBadgeClass(app.ready ? 'provisioned' : 'pending')}>
                  {app.ready ? 'ready' : 'baking'}
                </span>
                {active > 0 && (
                  <span style={{ color: 'var(--text-secondary)' }}>
                    <span aria-hidden="true">▶ </span>{active} live
                    {(staleVersionByApp.get(app.appId) ?? 0) > 0 && (
                      <span style={{ color: 'var(--text-tertiary, var(--text-secondary))' }}>
                        {' '}({staleVersionByApp.get(app.appId)} on prior version)
                      </span>
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </td>
    </tr>
  );
}
